
import { useState } from 'react';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useSimpleRateLimit } from '@/hooks/useSimpleRateLimit';
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import { handleSecureError, secureLog } from '@/utils/secureErrorHandler';
import { sanitizeAndValidateInput, enhancedSessionTitleSchema, audioFileSchema, ENHANCED_RATE_LIMITS } from '@/utils/enhancedInputValidation';
import { createAudioMixer } from '@/utils/audioMixingUtils';

export const useAudioStorage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { checkRateLimit } = useSimpleRateLimit();
  const { logSuspiciousActivity } = useSecurityMonitoring();
  const [uploading, setUploading] = useState(false);

  const saveAudioToStorage = async (
    audioBlob: Blob,
    sessionTitle: string,
    categoryId: string
  ): Promise<string | null> => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to save audio sessions.",
        variant: "destructive",
      });
      return null;
    }

    try {
      // Validate audio file
      const fileValidation = audioFileSchema.safeParse({
        size: audioBlob.size,
        type: audioBlob.type
      });

      if (!fileValidation.success) {
        toast({
          title: "Invalid File",
          description: fileValidation.error.errors[0].message,
          variant: "destructive",
        });
        return null;
      }

      // Validate and sanitize title
      const sanitizedTitle = sanitizeAndValidateInput(sessionTitle, 200);
      enhancedSessionTitleSchema.parse(sanitizedTitle);
      
      // Check rate limit with enhanced monitoring
      const canProceed = await checkRateLimit({ action: 'session_save', maxRequests: 10, windowMinutes: 60 });
      if (!canProceed) return null;

      setUploading(true);
      
      // Create a secure filename with additional validation
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const sanitizedCategory = categoryId.replace(/[^a-zA-Z0-9_-]/g, '');
      const sanitizedTitleForFile = sanitizedTitle.replace(/[^a-zA-Z0-9_-\s]/g, '').replace(/\s+/g, '-').substring(0, 50);
      
      // Additional security check for filename
      if (!sanitizedCategory || !sanitizedTitleForFile) {
        logSuspiciousActivity('invalid_filename_attempt', {
          originalCategory: categoryId,
          originalTitle: sessionTitle
        });
        throw new Error('Invalid filename components');
      }

      const filename = `${user.id}/${sanitizedCategory}/${timestamp}-${sanitizedTitleForFile}.mp3`;

      // Upload to Supabase storage with enhanced error handling and proper content type
      const { data, error } = await backendClient.storage
        .from('audio-sessions')
        .upload(filename, audioBlob, {
          contentType: audioBlob.type || 'audio/mpeg',
          upsert: false,
          duplex: 'half'
        });

      if (error) {
        console.error('Storage upload error:', error);
        throw error;
      }

      if (!data?.path) {
        throw new Error('Upload successful but no path returned');
      }

      // Get the public URL
      const { data: urlData } = backendClient.storage
        .from('audio-sessions')
        .getPublicUrl(data.path);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to generate public URL');
      }

      // Verify the uploaded file by making a HEAD request
      try {
        const verifyResponse = await fetch(urlData.publicUrl, { method: 'HEAD' });
        if (!verifyResponse.ok) {
          console.warn('⚠️ File uploaded but not immediately accessible');
        } else {
          const contentType = verifyResponse.headers.get('content-type');
          console.log('✅ File verified, content-type:', contentType);
        }
      } catch (verifyError) {
        console.warn('⚠️ Could not verify uploaded file:', verifyError);
      }

      secureLog('info', 'Audio saved to storage successfully', { 
        filename: data.path,
        size: audioBlob.size,
        contentType: audioBlob.type
      });
      
      return urlData.publicUrl;

    } catch (error: any) {
      const secureError = handleSecureError(error, 'audio_storage_save');
      
      // Log suspicious activity for certain error types
      if (error.message?.includes('Invalid') || error.message?.includes('validation')) {
        logSuspiciousActivity('invalid_audio_upload_attempt', {
          error: secureError.message,
          fileSize: audioBlob.size,
          fileType: audioBlob.type
        });
      }

      toast({
        title: "Storage Error",
        description: secureError.userMessage,
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const deleteAudioFromStorage = async (audioUrl: string): Promise<boolean> => {
    if (!user || !audioUrl.includes('audio-sessions')) {
      return false;
    }

    try {
      // Extract the file path from the URL
      const urlParts = audioUrl.split('/audio-sessions/');
      if (urlParts.length !== 2) return false;
      
      const filePath = urlParts[1];
      
      // Verify the file belongs to the current user
      if (!filePath.startsWith(user.id + '/')) {
        logSuspiciousActivity('unauthorized_delete_attempt', { 
          userId: user.id, 
          filePath,
          audioUrl: audioUrl.substring(0, 100) // Truncate URL for logging
        });
        return false;
      }

      const { error } = await backendClient.storage
        .from('audio-sessions')
        .remove([filePath]);

      if (error) {
        throw error;
      }

      secureLog('info', 'Audio deleted from storage successfully', { filePath });
      return true;
    } catch (error) {
      const secureError = handleSecureError(error, 'audio_storage_delete');
      secureLog('error', 'Failed to delete audio from storage', { error: secureError.message });
      return false;
    }
  };

  const saveMixedAudioToStorage = async (
    mainAudioUrl: string,
    backgroundAudioUrl: string,
    sessionTitle: string,
    categoryId: string,
    backgroundVolume: number = 0.6
  ): Promise<string | null> => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to save mixed audio sessions.",
        variant: "destructive",
      });
      return null;
    }

    try {
      // Validate and sanitize title
      const sanitizedTitle = sanitizeAndValidateInput(sessionTitle, 200);
      enhancedSessionTitleSchema.parse(sanitizedTitle);
      
      // Check rate limit
      const canProceed = await checkRateLimit({ action: 'mixed_session_save', maxRequests: 5, windowMinutes: 60 });
      if (!canProceed) return null;

      setUploading(true);
      
      // Create mixed audio blob using the audio mixer
      const audioMixer = createAudioMixer();
      const mixedBlob = await audioMixer.createMixedAudioBlob(
        mainAudioUrl,
        backgroundAudioUrl,
        {
          mainVolume: 1.0,
          backgroundVolume,
          outputFormat: 'wav'
        }
      );

      // Create a secure filename for mixed audio
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const sanitizedCategory = categoryId.replace(/[^a-zA-Z0-9_-]/g, '');
      const sanitizedTitleForFile = sanitizedTitle.replace(/[^a-zA-Z0-9_-\s]/g, '').replace(/\s+/g, '-').substring(0, 50);
      
      if (!sanitizedCategory || !sanitizedTitleForFile) {
        logSuspiciousActivity('invalid_mixed_filename_attempt', {
          originalCategory: categoryId,
          originalTitle: sessionTitle
        });
        throw new Error('Invalid filename components for mixed audio');
      }

      const filename = `${user.id}/${sanitizedCategory}/${timestamp}-${sanitizedTitleForFile}_MIXED.wav`;

      // Upload mixed audio to Supabase storage
      const { data, error } = await backendClient.storage
        .from('audio-sessions')
        .upload(filename, mixedBlob, {
          contentType: 'audio/wav',
          upsert: false,
          duplex: 'half'
        });

      if (error) {
        console.error('Mixed audio storage upload error:', error);
        throw error;
      }

      if (!data?.path) {
        throw new Error('Mixed audio upload successful but no path returned');
      }

      // Get the public URL
      const { data: urlData } = backendClient.storage
        .from('audio-sessions')
        .getPublicUrl(data.path);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to generate public URL for mixed audio');
      }

      secureLog('info', 'Mixed audio saved to storage successfully', { 
        filename: data.path,
        size: mixedBlob.size,
        contentType: 'audio/wav'
      });
      
      return urlData.publicUrl;

    } catch (error: any) {
      const secureError = handleSecureError(error, 'mixed_audio_storage_save');
      
      logSuspiciousActivity('mixed_audio_upload_failure', {
        error: secureError.message,
        mainAudioUrl: mainAudioUrl.substring(0, 100),
        backgroundAudioUrl: backgroundAudioUrl.substring(0, 100)
      });

      toast({
        title: "Mixed Audio Storage Error",
        description: secureError.userMessage,
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  return {
    saveAudioToStorage,
    saveMixedAudioToStorage,
    deleteAudioFromStorage,
    uploading
  };
};
